// Shared chrome for the public pages (app/art/vault-chip/page/index.src.html,
// .top and .foot): the wordmark, a static header in the page flow with
// Protection, Docs and Explore Vault, the app and docs links, and the footer.
// The entrance routes use the same header with the network pill in Explore
// Vault's place (app/art/welcome/PLAN.md section 4.0).
// Everything that navigates is a real link: the app link is wouter's <Link>,
// so a plain click moves client-side (an unlocked session is never reloaded)
// while a modified click or "open in new tab" behaves like any anchor.

import type { ReactNode } from 'react';
import { Link, useLocation } from 'wouter';
import { NetworkPill } from '../components/NetworkPill';
import { BUSINESS_EMAIL, SUPPORT_EMAIL, X_HANDLE, X_URL } from '../config';

export function ArrowUpRight({ className = 'icon' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M5 11 11 5M6 5h5v5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function ArrowRight({ className = 'icon' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M3 8h10M9 4l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** The home page's small "opens elsewhere" arrow (the prototype's 12-unit glyph). */
export function ArrowOut() {
  return (
    <svg viewBox="0 0 12 12" aria-hidden="true" focusable="false">
      <path d="M3 9l6-6M4 3h5v5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

/** "Enter app": the session-aware app entry (App.tsx computes the destination). */
export function AppLink({ href, className, children }: { href: string; className?: string; children: ReactNode }) {
  return (
    <Link href={href} className={className}>
      {children}
    </Link>
  );
}

/** A link to another site: new tab, no opener or referrer, and it says so to assistive technology. */
export function ExternalLink({ href, className, children }: { href: string; className?: string; children: ReactNode }) {
  return (
    <a href={href} className={className} target="_blank" rel="noopener noreferrer">
      {children}
      <span className="sr-only"> (opens in a new tab)</span>
    </a>
  );
}

type HeaderProps =
  | { variant?: 'site'; launchHref: string }
  /**
   * The entrance (/welcome, /locked, /create, /restore; app/art/welcome/PLAN.md section 4.0): the
   * network pill takes Explore Vault's slot, since Explore Vault would lead to the page already shown.
   * `secret` is for the steps where words or a passphrase exist: the wordmark is plain text and the
   * links are gone, so one header click cannot silently discard them; the pill stays.
   */
  | { variant: 'entrance'; secret?: boolean };

/**
 * The header: the wordmark, then Protection, Docs (hidden on phones) and Explore Vault in mint; on
 * the entrance, the network pill in Explore Vault's place.
 */
export function SiteHeader(props: HeaderProps) {
  const [location] = useLocation();
  const wordmark = (
    <Link href="/" className="wordmark" aria-label="ZeroQube, home">
      ZeroQube
    </Link>
  );
  const links = (
    <>
      <Link href="/protection" aria-current={location === '/protection' ? 'page' : undefined}>
        Docs
      </Link>
    </>
  );
  if (props.variant === 'entrance') {
    return (
      <header className={props.secret ? 'top secret' : 'top'}>
        {props.secret ? (
          <span className="wordmark">ZeroQube</span>
        ) : (
          <Link href="/" className="wordmark" aria-label="ZeroQube, home">
            ZeroQube
          </Link>
        )}
        <div className="top-end">
          {props.secret ? null : <nav aria-label="Primary">{links}</nav>}
          <NetworkPill />
        </div>
      </header>
    );
  }
  return (
    <header className="top">
      {wordmark}
      <nav aria-label="Primary">
        {links}
        <AppLink href={props.launchHref} className="go">
          Enter app
        </AppLink>
      </nav>
    </header>
  );
}

export function SiteFooter({ launchHref }: { launchHref: string }) {
  return (
    <footer className="foot">
      <span className="wordmark foot-wordmark">ZeroQube</span>
      <nav aria-label="Footer">
        <Link href="/protection">Docs</Link>
        <AppLink href={launchHref}>Enter app</AppLink>
      </nav>
      <address className="foot-contact">
        <span>
          Support <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </span>
        <span>
          Business <a href={`mailto:${BUSINESS_EMAIL}`}>{BUSINESS_EMAIL}</a>
        </span>
        <span>
          X <ExternalLink href={X_URL}>@{X_HANDLE}</ExternalLink>
        </span>
      </address>
      <span>Experimental · Unaudited</span>
    </footer>
  );
}
